import React, { useRef, useEffect } from "react";
import { OrbitControls as DreiOrbitControls } from "@react-three/drei";
import { OrbitControls as ThreeOrbitControls } from "three-stdlib";
import { Sphere } from "./Sphere.tsx";
import { Section } from "./constants.ts";

type SceneProps = {
  setActiveNav: React.Dispatch<React.SetStateAction<Section>>;
  setCoords: React.Dispatch<React.SetStateAction<string>>;
  setLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  scrollAngle: number;
  setScrollAngle: React.Dispatch<React.SetStateAction<number>>;
  setIsUserInteracting: React.Dispatch<React.SetStateAction<boolean>>;
  isUserInteracting: boolean;
};

export function Scene({
  setActiveNav,
  setCoords,
  setLoaded,
  scrollAngle,
  setScrollAngle,
  setIsUserInteracting,
  isUserInteracting
}: SceneProps) {
  const controlsRef = useRef<ThreeOrbitControls>(null);
  const pointerRef = useRef<{ x: number | null; y: number | null }>({ x: null, y: null });

  // Set loaded flag
  useEffect(() => {
    setLoaded(true);
  }, [setLoaded]);

  // Sync scrollAngle into controls
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.setAzimuthalAngle(scrollAngle);
      controlsRef.current.update();
    }
  }, [scrollAngle]);

  // Drag interaction to update azimuthal angle using both deltaX and deltaY
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;

    const handlePointerDown = (e: PointerEvent) => {
      setIsUserInteracting(true);
      pointerRef.current = { x: e.clientX, y: e.clientY };
    };

    const handlePointerMove = (e: PointerEvent) => {
      const { x, y } = pointerRef.current;
      if (x === null || y === null) return;

      const deltaX = e.clientX - x;
      const deltaY = e.clientY - y;
      pointerRef.current = { x: e.clientX, y: e.clientY };

      // Combine both deltas for horizontal rotation
      const combinedDelta = deltaX + deltaY;
      const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
      const multiplier = isTouchDevice ? 0.012 : 0.002; // mobile is more sensitive
      setScrollAngle((prev) => prev - combinedDelta * multiplier); // Tune multiplier as needed
    };

    const handlePointerUp = () => {
      setIsUserInteracting(false);
      pointerRef.current = { x: null, y: null };
      if (controls) {
        setScrollAngle(controls.getAzimuthalAngle());
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [setScrollAngle, setIsUserInteracting]);

  return (
    <>
      <hemisphereLight args={[0xf2e4d8, 0x201b17, 6]} />
      <directionalLight color={0xf2e4d8} intensity={4} position={[5, 10, 10]} castShadow />
      <directionalLight color={0xf2e4d8} intensity={0.5} position={[-5, -2, -5]} />
      <Sphere setActiveNav={setActiveNav} setCoords={setCoords} />
      <DreiOrbitControls
        ref={controlsRef}
        enableZoom={false}
        enablePan={false}
        enableDamping
        minPolarAngle={Math.PI / 2}
        maxPolarAngle={Math.PI / 2}
      />
    </>
  );
}
